import { useContext } from 'react';
import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardContext } from './DashboardContext';
import {
  DataMutationState,
  DataMutations,
  ProcessedMutationResults,
  MutationOptions,
  MutationResult,
  Registry,
} from '../models/DatamutationsModels';
import { processMutationResults } from '../utils/processMutationResults';
import isEmpty from 'lodash/isEmpty';

const DEFAULT_RESPONSE = {
  mutationData: {},
  errors: {},
  successes: {},
  pendings: {},
  isMutationPending: false,
  hasMutationError: false,
};
// kept existing logic
const promoteNestedData = (mr: MutationResult): unknown =>
  mr?.data != null && typeof mr.data === 'object' && !Array.isArray(mr.data) ? { ...mr, ...mr.data } : mr;

const fetchMutation = (m: MutationOptions): MutationResult => useMutation(m as any);

const fetchMutations = (mo: MutationOptions[]): MutationResult[] =>
  mo.map((m: MutationOptions) => promoteNestedData(fetchMutation(m)) as MutationResult);

const toMutationOptions = (
  dm: DataMutationState,
  state: string | undefined,
  DatasourceRegistry: Readonly<Registry>,
  queryClient: QueryClient,
): MutationOptions => ({
  mutationFn: async (requestObj: any) => {
    const mKey = [dm.type ?? '', { ...(dm.options ?? {}), state }];

    const [_key, options] = mKey;

    return await DatasourceRegistry[_key as string](
      {
        ...(options as Record<string, unknown>),
      },
      {
        ...(requestObj as unknown as Record<string, unknown>),
      },
    );
  },
  onSuccess: data => {
    if (dm.invalidateQueryType) {
      queryClient.invalidateQueries({
        queryKey: ['orionObject', { type: dm.invalidateQueryType }],
      });
    }
  },
});

export const useDashboardMutations = (
  mutations: DataMutations = {},
  state: string | undefined,
): ProcessedMutationResults => {
  if (isEmpty(mutations)) {
    return DEFAULT_RESPONSE;
  }

  const { DatasourceRegistry } = useContext(DashboardContext);
  const queryClient = useQueryClient();

  const mutationOptions = Object.values(mutations).map(value =>
    toMutationOptions(value, state, DatasourceRegistry, queryClient),
  );
  const fetchedMutationResults = fetchMutations(mutationOptions);
  return processMutationResults(fetchedMutationResults, mutations);
};
